import { Controller } from "@hotwired/stimulus"
import { get } from "@lib/requests"

// Connects to data-controller="credit-note-form"
export default class extends Controller {

  static targets = ["checkbox", "reason", "submit", "creditMessage", "selectAll", "quantity", "total"]
  static values = { 
    balance: Number,
    amountPaid: Number, 
    invoiceTotal: Number, 
    selected: Boolean 
  }

  connect() {
    // Set initial button status based on selected value and reason
    this.setButtonStatus()
  }

  updateStatuses() {
    this.setButtonStatus()
    this.setTotal()
    this.setCreditMessage()
  }
  
  toggleItem(event) {
    const row = event.target.closest(".flex-row")
    if (event.target.checked) {
      row.classList.remove("opacity-50")
      row.querySelector(".input").disabled = false
      row.querySelector(".revert-quantity").disabled = false
    } else {
      row.classList.add("opacity-50")
      row.querySelector(".input").disabled = true
      row.querySelector(".revert-quantity").disabled = true
      // this.revertQuantity(row)
    }
    this.updateStatuses()
    this.setSelectAllStatus(event)
  }

  toggleAll(event) {
    const section = event.target.closest(".bill-section")
    if (event.target.checked) {
      this.selectAllBoxes(section)
      this.enableAllInputs(section)
    } else {
      this.deselectAllBoxes(section)
      this.disableAllInputs(section)
    }
    this.updateStatuses()
  }

  selectAllBoxes(section) {
    const checkboxes = section.querySelectorAll("input[type='checkbox']")
    checkboxes.forEach(checkbox => {
      checkbox.checked = true
      checkbox.closest(".flex-row").classList.remove("opacity-50")
    })
  }

  deselectAllBoxes(section) {
    const checkboxes = section.querySelectorAll("input[type='checkbox']")
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
      checkbox.closest(".flex-row").classList.add("opacity-50")
    })
  }

  enableAllInputs(section) {
    const inputs = section.querySelectorAll("input")
    const revertButton = section.querySelector(".revert-quantity")
    inputs.forEach(input => input.disabled = false)
    revertButton.disabled = false
  }

  disableAllInputs(section) {
    const inputs = section.querySelectorAll("input[type='number']")
    const revertButton = section.querySelector(".revert-quantity")
    inputs.forEach(input => input.disabled = true)
    revertButton.disabled = true
  }

  setSelectAllStatus(event) {
    const section = event.target.closest(".bill-section")
    const selectAllCheckbox = section.querySelector("input[type='checkbox']")
    const totalItems = section.querySelectorAll("input[type='checkbox']").length
    const checkedItems = section.querySelectorAll("input[type='checkbox']:checked").length
    const status = checkedItems === totalItems ? "checked" : checkedItems > 0 ? "indeterminate" : "unchecked"
    if (status === "checked") {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    } else if (status === "indeterminate") {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = true
    } else {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    }
  }

  get checkedBoxes() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }
  
  get checkedItems() {
    return this.checkedBoxes.map(checkbox => checkbox.dataset.id)
  }
  
  get total() {
    const total_cents = this.checkedBoxes.reduce((acc, checkbox) => {
      const totalElement = checkbox.closest(".flex-row").querySelector(".item-total")
      return acc + parseFloat(totalElement.dataset.total)
    }, 0)
    const cents = Math.min(total_cents, this.invoiceTotalValue)
    return cents / 100
  }

  get formattedTotal() {
    const total = this.checkedBoxes.length === 0 ? 0 : this.total
    return total.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })
  }

  get creditAmount() {
    // const amount = Math.min(this.total, this.amountPaidValue / 100)
    // amount is however much more the total is than the balance
    return this.total - this.balanceValue / 100
  }

  get disabled() {
    return this.reasonTarget.value.length === 0 || this.checkedBoxes.length === 0
  }

  setButtonStatus() {
    this.submitTarget.disabled = this.disabled
  }

  setTotal() {
    const textContent = "Créer un avoir de " + this.formattedTotal
    this.submitTarget.textContent = textContent

    // Update the confirmation message when form changes
    let message = `Confirmer la création d'un avoir de ${this.formattedTotal} ?`
    // if (this.amountPaidValue > 0 && this.total > 0) {
    if (this.creditAmount > 0) {
      // const creditAmount = Math.min(this.total, this.amountPaidValue / 100)
      const formattedCredit = this.creditAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })
      message += ` Un crédit client de ${formattedCredit} sera aussi créé.`
    }
    this.submitTarget.dataset.turboConfirm = message
  }

  setCreditMessage() {
    if (!this.hasCreditMessageTarget) return
    // Initially hide the message while we calculate the amount
    this.creditMessageTarget.classList.add("hidden")
    const targetElement = this.creditMessageTarget.querySelector("p")
    const textContent = `Un crédit client de ${this.creditAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })} sera aussi créé (montant de l'avoir moins le montant restant à payer)`
    targetElement.textContent = textContent
    if (this.creditAmount > 0) {
      this.creditMessageTarget.classList.remove("hidden")
    }
  }

  updateQuantityStatus(resourceId, quantity) {
    const row = this.element.querySelector(`[data-resource-id="${resourceId}"]`)
    const input = row.querySelector("input")
    const initialQuantity = parseFloat(row.dataset.quantity, 10)

    if (quantity === 0 || (quantity > initialQuantity)) {
      input.classList.remove("bg-yellow-50")
      input.classList.add("bg-red-50")
    } else if (quantity !== initialQuantity) {
      input.classList.remove("bg-red-50")
      input.classList.add("bg-yellow-50")
    } else {
      input.classList.remove("bg-red-50")
      input.classList.remove("bg-yellow-50")
    }
  }
  
  async updateQuantity(event) {
    if (!event.target.value) return
    
    // Update the row's styling based on the new quantity
    const row = event.target.closest(".flex-row")
    const resourceId = row.dataset.resourceId
    const quantity = parseFloat(event.target.value, 10)

    // If the quantity is greater than the original quantity, we need to somehow prevent this
    if (quantity > row.dataset.quantity) {
      return this.resetQuantity(event)
    }
    
    this.updateQuantityStatus(resourceId, quantity)
    
    // Fetch the new total for the row
    const response = await get(`/api/v1/items/calculate_total?resource_id=${resourceId}&quantity=${quantity}`)
    const { total_gross_cents } = await response.json
    
    // Ensure the total is not greater than the row total
    const cents = Math.min(total_gross_cents, parseFloat(row.dataset.total, 10))
    
    // Update the row's total
    const totalElement = row.querySelector(".item-total")
    
    // Update the text content of the total element
    totalElement.textContent = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100)

    // Also update the dataset.total attribute on the element
    totalElement.dataset.total = cents
    
    // Update the hidden total input
    const totalInput = row.querySelector("[data-credit-note-form-target='totalInput']")
    if (totalInput) totalInput.value = cents
    
    // Update the statuses
    this.updateStatuses()
  }

  resetQuantity(event) {
    const row = event.target.closest(".flex-row")
    this.revertQuantity(row)
  }

  revertQuantity(row) {
    // Reset the input value to the original quantity stored in the item data-quantity attribute
    const input = row.querySelector("input")
    const quantity = parseFloat(row.dataset.quantity, 10)
    input.value = quantity
    this.updateQuantityStatus(row.dataset.resourceId, quantity)
    
    const totalElement = row.querySelector(".item-total")
    totalElement.textContent = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(row.dataset.total / 100)
    totalElement.dataset.total = row.dataset.total
    
    this.updateStatuses()
  }
  
}
